// src/components/InputField/InputField.tsx
import { FormLabel, forwardRef, Input, InputGroup, InputProps, Text } from '@chakra-ui/react';
import { Interpolation } from '@emotion/react';
import { ReactNode, useCallback } from 'react';
import { WithFormControl, WithFormControlProps } from '../WithFormControl';

export interface InputFieldProps extends InputProps {
  label?: ReactNode;
  rootProps?: WithFormControlProps;
  fullDatePickerArea?: boolean;
  helperText?: string;
  withoutFloating?: boolean;
}

const fullDatePickerAreaCss: Interpolation<InputProps> = {
  '&::-webkit-calendar-picker-indicator': {
    background: 'transparent',
    bottom: 0,
    color: 'transparent',
    cursor: 'pointer',
    height: 'auto',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    width: 'auto',
  },
};

export const InputField = forwardRef<InputFieldProps, 'input'>(
  ({ label, rootProps, fullDatePickerArea, helperText, withoutFloating, onChange, ...props }, ref) => {
    const handleDateChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (props.type === 'datetime-local') {
          const value = e.target.value;
          const [year, rest] = value.split('-');
          if (year && year.length > 4) {
            e.target.value = `${year.slice(0, 4)}-${rest || ''}`;
          }
        }
        onChange?.(e);
      },
      [onChange, props.type],
    );

    return (
      <WithFormControl variant={withoutFloating ? undefined : 'floating'} {...rootProps}>
        <InputGroup display='flex' flexDirection='column'>
          {label && (
            <FormLabel color='text.main' fontSize={withoutFloating ? '14px' : undefined} m={withoutFloating ? 0 : undefined}>
              {label}
            </FormLabel>
          )}
          <Input
            variant='flushed'
            placeholder=' '
            {...props}
            ref={ref}
            css={fullDatePickerArea ? fullDatePickerAreaCss : null}
            onChange={handleDateChange}
            min={
              props.type === 'datetime-local'
                ? (props.min ?? '1900-01-01T00:00')
                : props.min
            }
            max={
              props.type === 'datetime-local'
                ? (props.max ?? '9999-12-31T23:59')
                : props.max
            }
          />
          {helperText ? (
            <Text fontSize='12px' color='gray.500' mt={1}>
              {helperText}
            </Text>
          ) : null}
        </InputGroup>
      </WithFormControl>
    );
  },
);
